// src/pages/GeospatialHub.tsx — Geospatial Hub
//
// Geospatial is a first-class portal surface (peer of Dashboards) holding maps
// only. Two tiers, mirroring DashboardHub:
//   • Public  — the Geospatial Explorer (Postgres-backed), open to all users.
//   • Governed — Fabric-backed maps (Asset Explorer Map, …) via onboarding.
// Governed cards show a lock + "Request access" until granted (admins bypass).

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { mergeStyleSets } from "@fluentui/react";
import { CaretRight, LockSimple } from "@phosphor-icons/react";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";

const p = boroondaraPalette;

interface MapCard {
  title: string;
  description: string;
  color: string;
  route: string;
  tier: "public" | "governed";
  // governed cards gate on this tab_catalog item (geospatial:<item>)
  item?: string;
}

const cards: MapCard[] = [
  {
    title: "Geospatial Explorer",
    description:
      "Council layers on an interactive map — facilities, parks, playgrounds, wards and planning zones, with search and layer controls.",
    color: "#2e873d",
    route: "/geospatial/explorer",
    tier: "public",
  },
  {
    title: "Asset Explorer Map",
    description:
      "The council's Conquest asset estate on the map — trees, playgrounds, drainage, street furniture and more, with per-asset detail from the enterprise warehouse.",
    color: "#0F766E",
    route: "/geospatial/asset-map",
    tier: "governed",
    item: "asset_map",
  },
  {
    title: "Customer Request Map",
    description:
      "Geolocated customer service requests from Oracle CX — filter by service, status and ward to see where requests are coming from across the municipality.",
    color: "#6D28D9",
    route: "/geospatial/request-map",
    tier: "governed",
    item: "request_map",
  },
];

const styles = mergeStyleSets({
  page: {
    padding: 32,
    minHeight: "calc(100vh - 52px)",
    background: "#F5F5F5",
    "@media (max-width: 768px)": { padding: 16 },
  } as any,
  container: { maxWidth: 1240, margin: "0 auto" },
  headerRow: { marginBottom: 32 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 720, lineHeight: "1.6", fontSize: 14 },
  tabRow: { display: "flex", gap: 4, marginBottom: 24 },
  tab: {
    padding: "8px 18px", borderRadius: 4, fontSize: 13, fontWeight: 600 as any,
    border: "1px solid #EDEDED", background: "#FFFFFF", color: "#484848",
    cursor: "pointer", transition: "all 0.15s",
  },
  tabActive: { background: "#d9f4d7", borderColor: "rgba(46,135,61,0.4)", color: "#2e873d" },
  grid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 20, "@media (max-width: 768px)": { gridTemplateColumns: "1fr" },
  } as any,
  card: {
    background: "#FFFFFF", borderRadius: 10, padding: "28px 24px",
    border: "1px solid #E1E1E1", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    cursor: "pointer", transition: "all 0.15s ease",
    display: "flex", flexDirection: "column", gap: 14,
    selectors: {
      ":hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)", transform: "translateY(-2px)", borderColor: "#C4C9D0" },
    },
  } as any,
  cardLocked: { cursor: "default", selectors: { ":hover": { transform: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", borderColor: "#E1E1E1" } } } as any,
  cardTitleRow: { display: "flex", alignItems: "center", gap: 8 },
  cardTitle: { fontSize: 17, fontWeight: 700 as any, color: p.text },
  cardDesc: { fontSize: 13, color: p.text3, lineHeight: "1.5" },
  cardArrow: { marginTop: "auto", fontSize: 12, fontWeight: 600 as any, display: "flex", alignItems: "center", gap: 4 },
  lockPill: {
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600 as any,
    color: "#92400E", background: "#FEF3C7", borderRadius: 20, padding: "2px 10px",
  },
  accessDenied: {
    padding: 56, textAlign: "center" as const, color: p.text3, fontSize: 14, lineHeight: "1.6",
    background: "#FFFFFF", borderRadius: 10, border: "1px solid #EDEDED",
  },
  comingSoonTitle: { color: p.text, fontWeight: 700 as any, fontSize: 18, marginBottom: 8 },
});

type Tier = "public" | "governed";

export default function GeospatialHub() {
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState<Tier>("public");
  const [isAdmin, setIsAdmin] = useState(false);
  const [allowed, setAllowed] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedGeospatial?: string[] }>("/me");
        if (!alive) return;
        setIsAdmin(res.data.role === "admin");
        setAllowed(Array.isArray(res.data.allowedGeospatial) ? res.data.allowedGeospatial : []);
      } catch {
        /* default: no governed access */
      }
    })();
    return () => { alive = false; };
  }, []);

  const canOpen = (c: MapCard) => c.tier === "public" || isAdmin || (c.item ? allowed.includes(c.item) : false);
  const filtered = cards.filter((c) => c.tier === activeTab);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <div className={styles.title}>Geospatial</div>
          <div className={styles.subtitle}>
            Explore council data on interactive maps. Public maps are open to everyone; governed maps draw
            on enterprise data and require access.
          </div>
        </div>

        <div className={styles.tabRow}>
          <button
            className={`${styles.tab} ${activeTab === "public" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("public")}
          >
            Public
          </button>
          <button
            className={`${styles.tab} ${activeTab === "governed" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("governed")}
          >
            Governed
          </button>
        </div>

        {activeTab === "public" && (
          <div className={styles.grid}>
            {filtered.map((card) => (
              <div
                key={card.route}
                className={styles.card}
                onClick={() => nav(card.route)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && nav(card.route)}
              >
                <div className={styles.cardTitleRow}>
                  <div className={styles.cardTitle}>{card.title}</div>
                </div>
                <div className={styles.cardDesc}>{card.description}</div>
                <div className={styles.cardArrow} style={{ color: card.color }}>
                  Open map <CaretRight size={10} weight="bold" />
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "governed" && (
          <div className={styles.grid}>
            {filtered.map((card) => {
              const open = canOpen(card);
              return (
                <div
                  key={card.route}
                  className={`${styles.card} ${open ? "" : styles.cardLocked}`}
                  onClick={() => open && nav(card.route)}
                  role={open ? "button" : undefined}
                  tabIndex={open ? 0 : undefined}
                  onKeyDown={(e) => open && e.key === "Enter" && nav(card.route)}
                >
                  <div className={styles.cardTitleRow}>
                    <div className={styles.cardTitle}>{card.title}</div>
                    {!open && (
                      <span className={styles.lockPill}>
                        <LockSimple size={11} weight="bold" /> Access required
                      </span>
                    )}
                  </div>
                  <div className={styles.cardDesc}>{card.description}</div>
                  {open ? (
                    <div className={styles.cardArrow} style={{ color: card.color }}>
                      Open map <CaretRight size={10} weight="bold" />
                    </div>
                  ) : (
                    <div
                      className={styles.cardArrow}
                      style={{ color: "#92400E", cursor: "pointer" }}
                      onClick={(e) => { e.stopPropagation(); nav("/onboard"); }}
                    >
                      Request access <CaretRight size={10} weight="bold" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
